import { Suspense, lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { createBrowserRouter } from 'react-router-dom';

import InitializationGuard from '@/app/app-router/guards/InitializationGuard';

import {
  AuthenticatedLayoutWithNavbar,
  AuthenticatedLayoutWithSidebar,
} from '@/widgets/layouts';

import { FullScreenLoader } from '@/shared/ui';

import { AppRoutes } from './AppRoutes';
import HomePage from './HomePage';
import {
  AuthenticatedGuard,
  PermissionGuard,
  UnauthenticatedGuard,
} from './guards';
import {
  authRouter,
  commonRouter,
  formsRouter,
  invitationRouter,
  organizationsRouter,
  permissionRouter,
  roleRouter,
  userRouter,
} from './routers';
import type { RouteProtectedWithPermissionObject } from './types';

const HrmRouter = lazy(() => import('hrm/HrmRouter'));

const tenantRoutes: RouteProtectedWithPermissionObject[] =
  !window.isCurrentHostEqualsCore
    ? [...userRouter, ...roleRouter, ...permissionRouter, ...invitationRouter]
    : [];

const wrapWithSuspense = (element: React.ReactNode) => (
  <Suspense fallback={<FullScreenLoader />}>{element}</Suspense>
);

const wrapWithPermissionGuard = (route: RouteProtectedWithPermissionObject) =>
  route.permissions ? (
    <PermissionGuard permissions={route.permissions}>
      {wrapWithSuspense(route.element)}
    </PermissionGuard>
  ) : (
    wrapWithSuspense(route.element)
  );

const authorizedRoutes: RouteObject[] = [
  {
    element: <AuthenticatedGuard />,
    children: [
      {
        element: <AuthenticatedLayoutWithNavbar />,
        children: [
          {
            path: '/hrm/*',
            element: wrapWithSuspense(<HrmRouter />),
          },
        ],
      },

      {
        element: <AuthenticatedLayoutWithSidebar />,
        children: [
          {
            path: AppRoutes.HOME,
            element: <HomePage />,
          },
          ...tenantRoutes,
          ...organizationsRouter,
          ...formsRouter,
        ].map((route: RouteProtectedWithPermissionObject) => ({
          ...route,
          element: wrapWithPermissionGuard(route),
        })),
      },
    ],
  },
];

const unauthorizedRoutes: RouteObject[] = [
  {
    element: <UnauthenticatedGuard />,
    children: authRouter.map((route) => ({
      ...route,
      element: wrapWithSuspense(route.element),
    })),
  },
];

const commonRoutes: RouteObject[] = commonRouter.map((route) => ({
  ...route,
  element: wrapWithSuspense(route.element),
}));

export const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: <InitializationGuard />,
    children: [...authorizedRoutes, ...unauthorizedRoutes],
  },
  ...commonRoutes,
];

export const appRouter = createBrowserRouter(appRoutes);
